/**
 * Profile Setup Page - 5-screen onboarding
 *
 * Screen 1: Career Stage (radio list, required)
 * Screen 2: Monthly Burn (currency, required, non-zero)
 * Screen 3: Monthly Capacity (numeric, required, non-zero)
 * Screen 4: Location (text, required)
 * Screen 5: Setup Complete (single CTA to dashboard)
 *
 * Rules: Forward only, no skip, save after each screen
 */

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts';
import { CareerStageKey, CAREER_STAGES, CAREER_STAGE_KEYS } from '@/types';

export function ProfileSetupPage() {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { updateProfile } = useAuth();
  const [keyboardOpen, setKeyboardOpen] = useState(false);

  // Get next destination from query param
  const params = new URLSearchParams(routerLocation.search);
  const nextDestination = params.get('next') || '/app/dashboard';

  // Detect keyboard via visualViewport
  useEffect(() => {
    const handleResize = () => {
      if (window.visualViewport) {
        const heightDiff = window.innerHeight - window.visualViewport.height;
        setKeyboardOpen(heightDiff > 150);
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
      return () => window.visualViewport?.removeEventListener('resize', handleResize);
    }
  }, []);

  // Form state - no pre-filled defaults visible
  const [selectedStage, setSelectedStage] = useState<CareerStageKey | null>(null);
  const [monthlyBurn, setMonthlyBurn] = useState<string>('');
  const [showExamples, setShowExamples] = useState(false);
  const [monthlyCapacity, setMonthlyCapacity] = useState<string>('');
  const [location, setLocation] = useState<string>('');

  // UI state
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  // Parse burn value (store as cents)
  const burnCents = Math.round(parseFloat(monthlyBurn || '0') * 100);

  // Validation - strict requirements
  const canProceedStep1 = selectedStage !== null;
  const canProceedStep2 = burnCents > 0;
  const capacityValue = parseInt(monthlyCapacity || '0', 10);
  const canProceedStep3 = capacityValue > 0;
  const canProceedStep4 = location.trim().length > 0;

  // Save career stage and advance to step 2
  const handleStep1Complete = async () => {
    if (!selectedStage) return;
    setIsLoading(true);
    try {
      await updateProfile({ careerStage: selectedStage });
      setStep(2);
    } catch (error) {
      console.error('Error saving career stage:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Save monthly burn and advance to step 3
  const handleStep2Complete = async () => {
    if (burnCents <= 0) return;
    setIsLoading(true);
    try {
      await updateProfile({ monthlyBurn: burnCents });
      setStep(3);
    } catch (error) {
      console.error('Error saving monthly burn:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Save monthly capacity and advance to step 4
  const handleStep3Complete = async () => {
    if (capacityValue <= 0) return;
    setIsLoading(true);
    try {
      await updateProfile({ monthlyCapacity: capacityValue });
      setStep(4);
    } catch (error) {
      console.error('Error saving monthly capacity:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Save location and complete onboarding, advance to step 5
  const handleStep4Complete = async () => {
    if (!location.trim()) return;
    setIsLoading(true);
    try {
      await updateProfile({
        location: location.trim(),
        onboardingCompleted: true,
      });
      setStep(5);
    } catch (error) {
      console.error('Error saving location:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Navigate to next destination (or result if from save flow)
  const handleStep5Complete = () => {
    const postAuthAction = sessionStorage.getItem('postAuthAction');
    if (postAuthAction === 'save') {
      sessionStorage.removeItem('postAuthAction');
      navigate('/app/result', { replace: true });
    } else {
      navigate(nextDestination, { replace: true });
    }
  };

  const handleContinue = () => {
    if (step === 1) handleStep1Complete();
    else if (step === 2) handleStep2Complete();
    else if (step === 3) handleStep3Complete();
    else if (step === 4) handleStep4Complete();
    else handleStep5Complete();
  };

  const canContinue =
    step === 1 ? canProceedStep1 :
    step === 2 ? canProceedStep2 :
    step === 3 ? canProceedStep3 :
    step === 4 ? canProceedStep4 :
    true; // Step 5 always enabled

  // Step content
  const stepContent = {
    1: { title: 'Career Stage' },
    2: { title: 'Monthly Burn' },
    3: { title: 'Monthly Capacity' },
    4: { title: 'Location' },
    5: { title: '' }, // No title for completion screen
  };
  
  return (
    <div className="min-h-screen min-h-[100dvh] bg-cream-100 flex flex-col">
      {/* Header - no back button (forward only), hidden on step 5 */}
      {step <= 4 && (
        <header className="flex items-center justify-center px-4 py-3">
          <h1 className="text-lg font-semibold text-charcoal-800">
            {stepContent[step as keyof typeof stepContent].title}
          </h1>
        </header>
      )}

      {/* Progress bar - hidden on step 5 */}
      {step <= 4 && (
        <div className="px-4 mb-6">
          <div className="h-1 bg-charcoal-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-teal-500 transition-all duration-300"
              style={{ width: `${(step / 4) * 100}%` }}
            />
          </div>
          <p className="text-xs text-charcoal-400 text-right mt-1">{step}/4</p>
        </div>
      )}

      {/* Content */}
      <main className="flex-1 flex flex-col px-6 overflow-y-auto">
        {/* Screen 1: Career Stage */}
        {step === 1 && (
          <div className="max-w-sm mx-auto w-full">
            <p className="text-center text-charcoal-500 text-sm mb-6">
              Select your current stage
            </p>

            {/* Radio list - all options visually equal */}
            <div className="space-y-3">
              {CAREER_STAGE_KEYS.map((key) => {
                const stage = CAREER_STAGES[key];
                const isSelected = selectedStage === key;

                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setSelectedStage(key)}
                    className={`w-full p-4 rounded-xl text-left transition-all ${
                      isSelected
                        ? 'bg-teal-50 border-2 border-teal-500'
                        : 'bg-white border border-charcoal-200 hover:border-charcoal-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {/* Radio circle */}
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          isSelected ? 'border-teal-500 bg-teal-500' : 'border-charcoal-300'
                        }`}
                      >
                        {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                      </div>

                      {/* Label only - no hierarchy */}
                      <span className={`font-medium ${isSelected ? 'text-teal-700' : 'text-charcoal-700'}`}>
                        {stage.label.replace(' Artist', '')}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Screen 2: Monthly Burn */}
        {step === 2 && (
          <div className="max-w-sm mx-auto w-full">
            <p className="text-center text-charcoal-500 text-sm mb-6">
              Your total monthly expenses
            </p>

            {/* Currency input - no placeholder */}
            <div>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-charcoal-400 text-xl">$</span>
                <input
                  type="number"
                  inputMode="decimal"
                  value={monthlyBurn}
                  onChange={(e) => setMonthlyBurn(e.target.value)}
                  onFocus={() => setShowExamples(true)}
                  onBlur={() => setTimeout(() => setShowExamples(false), 200)}
                  placeholder=""
                  min="1"
                  className="w-full h-14 pl-10 pr-4 text-center text-2xl font-semibold text-charcoal-700 bg-cream-100 border border-charcoal-200 rounded-xl focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 transition-all"
                />
              </div>

              {/* Examples - shown on focus only */}
              {showExamples && (
                <div className="mt-3 p-3 bg-charcoal-50 rounded-lg">
                  <p className="text-xs text-charcoal-500 mb-2">Examples:</p>
                  <div className="flex flex-wrap gap-2">
                    {[1500, 3000, 5000, 8000].map((amount) => (
                      <button
                        key={amount}
                        type="button"
                        onClick={() => setMonthlyBurn(String(amount))}
                        className="px-3 py-1.5 text-sm bg-cream-100 border border-charcoal-200 rounded-lg text-charcoal-600 hover:border-charcoal-300 active:bg-charcoal-50"
                      >
                        ${amount.toLocaleString()}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Validation message */}
              {monthlyBurn && burnCents === 0 && (
                <p className="text-xs text-red-500 text-center mt-2">
                  Amount must be greater than zero
                </p>
              )}
            </div>
          </div>
        )}

        {/* Screen 3: Monthly Capacity */}
        {step === 3 && (
          <div className="max-w-sm mx-auto w-full">
            <p className="text-center text-charcoal-500 text-sm mb-6">
              How many works can you realistically complete in a month?
            </p>

            <div>
              <div className="relative">
                <input
                  type="number"
                  inputMode="numeric"
                  value={monthlyCapacity}
                  onChange={(e) => setMonthlyCapacity(e.target.value)}
                  placeholder=""
                  min="1"
                  className="w-full h-14 px-4 text-center text-2xl font-semibold text-charcoal-700 bg-cream-100 border border-charcoal-200 rounded-xl focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 transition-all"
                />
              </div>

              {/* Validation message */}
              {monthlyCapacity && capacityValue === 0 && (
                <p className="text-xs text-red-500 text-center mt-2">
                  Enter a number greater than zero
                </p>
              )}
            </div>
          </div>
        )}

        {/* Screen 4: Location */}
        {step === 4 && (
          <div className="max-w-sm mx-auto w-full">
            <p className="text-center text-charcoal-500 text-sm mb-6">
              Where are you based?
            </p>

            <div>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="City, Country"
                className="w-full h-14 px-4 text-center text-lg text-charcoal-700 bg-cream-100 border border-charcoal-200 rounded-xl focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 transition-all"
              />
            </div>
          </div>
        )}

        {/* Screen 5: Setup Complete */}
        {step === 5 && (
          <div className="flex-1 flex items-center justify-center">
            <h2 className="text-2xl font-bold text-charcoal-800">
              Setup complete
            </h2>
          </div>
        )}

        {/* Spacer for fixed button */}
        <div className={`flex-shrink-0 transition-all duration-200 ${keyboardOpen ? 'h-16' : 'h-24'}`} />
      </main>

      {/* Fixed bottom button */}
      <button
        onClick={handleContinue}
        disabled={!canContinue || isLoading}
        className={`fixed bottom-0 inset-x-0 z-40 w-full text-base font-semibold transition-all duration-200 ${
          keyboardOpen
            ? 'min-h-[52px] pt-3 pb-3'
            : 'min-h-[80px] pt-6 pb-[max(1.5rem,env(safe-area-inset-bottom))]'
        } ${
          canContinue && !isLoading
            ? 'bg-teal-500 text-white active:bg-teal-600'
            : 'bg-charcoal-100 text-charcoal-400'
        }`}
      >
        {isLoading ? 'Saving...' : step === 5 ? 'Go to dashboard' : 'Continue'}
      </button>
    </div>
  );
}

export default ProfileSetupPage;
